/**
 * Conversation management utilities for AI Prescription
 */

import { ConversationItem } from './types';
import { checkPrescriptionSent } from './prescriptionUtils';

/**
 * In-memory hold for the in-progress AI Prescription conversation — a live external
 * store (see `subscribeConversationState`/`getHeldConversations`), NOT a mount-time
 * snapshot.
 *
 * Deliberately a module-level variable, NOT sessionStorage/localStorage:
 * - It survives the AIPrescription component unmounting/remounting during SPA
 *   navigation (switching tabs to Medication History, Favourite Drugs, etc. and
 *   coming back) because the module stays loaded.
 * - Crucially, the WebSocket message handler writes into THIS store directly (via
 *   `updateHeldConversations`), not into component-local React state. So a response
 *   that finishes streaming while the user is on a different tab still lands here
 *   instead of being silently dropped by an unmounted component's stale setState —
 *   the agent's turn is never aborted by navigation, and the AI Prescription tab
 *   shows the completed answer whenever the user comes back to it.
 * - It is wiped by a full page reload (browser refresh), since the JS context is
 *   recreated from scratch — refresh therefore starts a fresh conversation.
 * - Nothing clinical (PHI) is ever written to browser storage, matching
 *   `patientSession` and `searchHistoryUtils`.
 */
interface HeldConversationState {
  patientId: string;
  showResults: boolean;
  conversations: ConversationItem[];
  draftInputText: string;
}

let heldConversation: HeldConversationState = {
  patientId: '',
  showResults: false,
  conversations: [],
  draftInputText: '',
};

type Listener = () => void;
const listeners = new Set<Listener>();
const notify = (): void => listeners.forEach((listener) => listener());

/** Subscribe to changes in the held conversation (for `useSyncExternalStore`). */
export const subscribeConversationState = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Live snapshot getters — stable function identities for `useSyncExternalStore`. */
export const getHeldConversations = (): ConversationItem[] => heldConversation.conversations;
export const getHeldShowResults = (): boolean => heldConversation.showResults;
export const getDraftInputText = (): string => {
  console.log('[DRAFT-DIAGNOSTIC] getDraftInputText() → returning:', JSON.stringify(heldConversation.draftInputText));
  return heldConversation.draftInputText;
};

/**
 * Reconcile the held store with the currently active patient. A different patient
 * than the one currently held resets to an empty conversation so PHI never leaks
 * across patients; the same patient is left untouched (including anything the
 * WebSocket handler wrote in while this page was unmounted).
 *
 * CRITICAL: Only reset when we have POSITIVE EVIDENCE of a patient change.
 * Do NOT reset when patientId is temporarily empty (e.g., during component remount
 * before patient context is re-established). Empty patientId is different from a
 * confirmed patient change. Resetting on empty breaks WebSocket recovery.
 */
export const ensureHeldStateForPatient = (patientId: string): void => {
  const currentId = patientId || '';
  const storedId = heldConversation.patientId || '';

  console.log('[DRAFT-DIAGNOSTIC] ensureHeldStateForPatient() called with:', currentId, '| stored patient:', storedId, '| current draft:', JSON.stringify(heldConversation.draftInputText));

  // Case 1: Patient ID is empty - don't reset
  // This is a temporary unavailable patient ID, not a patient change.
  // Let WebSocket recovery handle the state later.
  if (!currentId) {
    console.log('[DRAFT-DIAGNOSTIC] ensureHeldStateForPatient() → currentId empty, NO-OP');
    return;
  }

  // Case 2: Patient ID matches - keep state
  if (currentId === storedId) {
    console.log('[DRAFT-DIAGNOSTIC] ensureHeldStateForPatient() → same patient, keeping state');
    return;
  }

  // Case 3: Patient ID is non-empty and different - confirmed patient change
  // Reset to protect PHI across patients, including unsent draft text
  console.log('[DRAFT-DIAGNOSTIC] ensureHeldStateForPatient() → PATIENT CHANGED, clearing draft');
  heldConversation = { patientId: currentId, showResults: false, conversations: [], draftInputText: '' };
  notify();
};

const normMedName = (value: unknown): string =>
  (typeof value === 'string' ? value : '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Each conversation card's `prescriptions` is a frozen snapshot taken when that
 * turn's payload was built (see createPrescriptionsFromMedicationData) — a card
 * created BEFORE a drug was actually sent never learns that a LATER turn
 * confirmed the send, so its Send button would stay active and re-clickable
 * indefinitely (double-submission risk on send2Pharmacy). This is the single
 * place that back-fills `sent: true` onto earlier cards for the same
 * medication name once any conversation in the array confirms it.
 *
 * Deliberately BACKWARD only (never forward): only conversations with an id
 * (creation timestamp) <= the confirming conversation's id are patched, so a
 * genuinely NEW prescription request for the same drug name later in the same
 * session is untouched and gets its own fresh `sent` status from its own turn.
 */
const applyRetroactiveSentStatus = (list: ConversationItem[]): ConversationItem[] => {
  const confirmedByName = new Map<string, number>();
  const registerConfirmed = (name: string, sourceId: number): void => {
    if (!name) return;
    const existing = confirmedByName.get(name);
    if (existing === undefined || sourceId < existing) {
      confirmedByName.set(name, sourceId);
    }
  };

  // Most recent conversation whose `prescriptions` actually named the drug(s)
  // being discussed — carried forward so a later turn's send-confirmation TEXT
  // (see below) can be attributed to a name, even though the backend typically
  // returns an empty `medication_data` on the confirming turn itself.
  let lastNamedPrescriptions: any[] | null = null;

  for (const c of list) {
    const hasStructuralSentSignal =
      Array.isArray(c.sentMedicationNames) && c.sentMedicationNames.length > 0;

    if (Array.isArray(c.prescriptions) && c.prescriptions.length > 0) {
      lastNamedPrescriptions = c.prescriptions as any[];
      for (const p of c.prescriptions as any[]) {
        if (p?.sent === true) {
          registerConfirmed(normMedName(p.medication_name || p.drugName), c.id);
        }
      }
    } else if (checkPrescriptionSent(c.aiResponse || '') && lastNamedPrescriptions && !hasStructuralSentSignal) {
      // Fallback for turns predating/lacking the structural `sentMedicationNames` signal
      // below: the backend's own structured payload went empty on this turn (its own card
      // is correctly hidden via ui_blocks/current_state gating elsewhere), so the response
      // TEXT is the only signal here — same detection the send API path already relies on
      // (see prescriptionCallbacks.ts). Attribute it to the most recent turn that actually
      // named the drug(s). Guarded on `!hasStructuralSentSignal` (a PARTIAL send's
      // confirming turn always carries `sent_medication_data`, handled below instead) so
      // this blind "every named prescription" attribution only ever fires when the send
      // really was total — nothing was left unsent to wrongly mark.
      for (const p of lastNamedPrescriptions) {
        registerConfirmed(normMedName(p?.medication_name || p?.drugName), c.id);
      }
    }

    if (hasStructuralSentSignal) {
      // Structural signal (server `sent_medication_data`) naming EXACTLY which
      // medication(s) this turn's send transmitted — present whether the send was full or
      // partial (e.g. "send Coumadin" out of a 2-drug draft, where `c.prescriptions` above
      // is simultaneously non-empty because Abilify is still an active, unsent draft row).
      // Applied independently of the branch above so only these names are confirmed,
      // never a sibling medication left unsent in the same draft.
      for (const name of c.sentMedicationNames as string[]) {
        registerConfirmed(normMedName(name), c.id);
      }
    }
  }
  if (confirmedByName.size === 0) return list;

  const matchedNames = new Set<string>();
  let anyChanged = false;
  const patched = list.map((c) => {
    if (!Array.isArray(c.prescriptions) || c.prescriptions.length === 0) return c;
    let localChanged = false;
    const updatedPrescriptions = (c.prescriptions as any[]).map((p) => {
      const name = normMedName(p?.medication_name || p?.drugName);
      if (p?.sent === true) {
        if (name) matchedNames.add(name);
        return p;
      }
      if (!name) return p;
      const sourceId = confirmedByName.get(name);
      if (sourceId === undefined || c.id > sourceId) return p;
      matchedNames.add(name);
      localChanged = true;
      return { ...p, sent: true };
    });
    if (!localChanged) return c;
    anyChanged = true;
    return { ...c, prescriptions: updatedPrescriptions };
  });

  // Names the server confirmed as sent (via `sentMedicationNames`) but that never matched
  // ANY prescription row anywhere in the held history — e.g. the drug's draft card was
  // created before this browser session's held conversation list existed (a reload wiped
  // `heldConversation`), so there is nothing earlier for the backward patch above to find.
  // The confirming turn's own `medication_data` is typically emptied by the backend (see
  // module docstring), so without this fallback no card anywhere ever shows "sent," even
  // though the send genuinely succeeded. Attach a synthesized/found row directly onto the
  // confirming turn's OWN card instead of silently dropping the confirmation.
  const unmatchedNames = new Set<string>();
  confirmedByName.forEach((_sourceId, name) => {
    if (!matchedNames.has(name)) unmatchedNames.add(name);
  });
  if (unmatchedNames.size === 0) return anyChanged ? patched : list;

  // Best-effort template for a synthesized row: reuse the fullest prescription details
  // available anywhere in history for that name (dosage/SIG/etc.), so the fallback card
  // isn't a bare name with every other field blank when a fuller snapshot exists.
  const templateByName = new Map<string, any>();
  for (const c of list) {
    if (!Array.isArray(c.prescriptions)) continue;
    for (const p of c.prescriptions as any[]) {
      const name = normMedName(p?.medication_name || p?.drugName);
      if (name && unmatchedNames.has(name) && !templateByName.has(name)) {
        templateByName.set(name, p);
      }
    }
  }

  let fallbackApplied = false;
  const withFallback = patched.map((c) => {
    if (!Array.isArray(c.sentMedicationNames) || c.sentMedicationNames.length === 0) return c;
    // Keep the original (non-normalized) display casing for the no-template case below —
    // normMedName lowercases purely for matching, not for what the user sees on the card.
    const namesToAttach = (c.sentMedicationNames as string[])
      .map((original) => ({ normalized: normMedName(original), original }))
      .filter(({ normalized }) => unmatchedNames.has(normalized));
    if (namesToAttach.length === 0) return c;

    const existing = Array.isArray(c.prescriptions) ? (c.prescriptions as any[]) : [];
    const existingNames = new Set(
      existing.map((p) => normMedName(p?.medication_name || p?.drugName))
    );
    const synthesized = namesToAttach
      .filter(({ normalized }) => !existingNames.has(normalized))
      .map(({ normalized, original }) => {
        const template = templateByName.get(normalized);
        return template
          ? { ...template, sent: true }
          : { medication_name: original, sent: true };
      });
    if (synthesized.length === 0) return c;
    synthesized.forEach((p) => matchedNames.add(normMedName(p.medication_name)));
    fallbackApplied = true;
    return { ...c, prescriptions: [...existing, ...synthesized] };
  });

  return anyChanged || fallbackApplied ? withFallback : list;
};

/**
 * Apply a conversation update. Safe to call whether or not the AI Prescription page
 * is currently mounted — the WebSocket message handler registers its callback for
 * the app's lifetime and keeps calling this on every incoming message, mounted or
 * not, so an in-flight turn always finishes into this store.
 */
export const updateHeldConversations = (
  updater: ConversationItem[] | ((prev: ConversationItem[]) => ConversationItem[])
): void => {
  const next =
    typeof updater === 'function'
      ? (updater as (prev: ConversationItem[]) => ConversationItem[])(heldConversation.conversations)
      : updater;
  if (next === heldConversation.conversations) return;
  heldConversation = { ...heldConversation, conversations: applyRetroactiveSentStatus(next) };
  notify();
};

export const setHeldShowResults = (showResults: boolean): void => {
  if (heldConversation.showResults === showResults) return;
  heldConversation = { ...heldConversation, showResults };
  notify();
};

/** Persist unsent draft input text across component unmounting. */
export const setDraftInputText = (text: string): void => {
  console.log('[DRAFT-DIAGNOSTIC] setDraftInputText() called with:', JSON.stringify(text), '| previous was:', JSON.stringify(heldConversation.draftInputText));
  if (heldConversation.draftInputText === text) {
    console.log('[DRAFT-DIAGNOSTIC] setDraftInputText() → no-op (same value)');
    return;
  }
  heldConversation = { ...heldConversation, draftInputText: text };
  console.log('[DRAFT-DIAGNOSTIC] setDraftInputText() → stored, now heldConversation.draftInputText =', JSON.stringify(heldConversation.draftInputText));
  notify();
};

/** Drop the held conversation and draft input (e.g. on logout / session timeout / explicit clear). */
export const clearConversationState = (): void => {
  heldConversation = { patientId: '', showResults: false, conversations: [], draftInputText: '' };
  notify();
};

/**
 * Finds the target conversation (loading conversation or most recent)
 */
export const findTargetConversation = (conversations: ConversationItem[]): ConversationItem | null => {
  if (conversations.length === 0) {
    return null;
  }

  // First try to find loading conversations
  const loadingConversations = conversations.filter(conv => conv.isLoading);

  if (loadingConversations.length > 0) {
    return loadingConversations[loadingConversations.length - 1];
  } else {
    // If no loading conversations, get the most recent conversation
    return conversations[conversations.length - 1];
  }
};

/**
 * Updates a conversation by ID
 */
export const updateConversationById = (
  conversations: ConversationItem[],
  conversationId: number,
  updates: Partial<ConversationItem> | ((conv: ConversationItem) => ConversationItem),
  logMessage?: string
): ConversationItem[] => {
  return conversations.map((conv) => {
    // Use both strict and loose comparison to handle any type mismatches
    const idMatches = conv.id === conversationId ||
      String(conv.id) === String(conversationId) ||
      Number(conv.id) === Number(conversationId);

    if (idMatches) {
      const next = typeof updates === 'function' ? updates(conv) : { ...conv, ...updates };
      // Drop live status steps once the turn is no longer loading.
      if (!next.isLoading && next.statusMessages !== undefined) {
        return { ...next, statusMessages: undefined };
      }
      return next;
    }
    return conv;
  });
};

